import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FileDown, Filter, Plus, X } from 'lucide-react';
import { SPOT_FILTERS, daysOf, spotCount } from './programAdminModel';
import { DayTabsBar } from './ProgramAdminHeader';
import ProgramList from './ProgramList';
import SidePanel from './SidePanel';
import { PdfPagesEntries } from './PdfPagesField';
import {
  ActionBar, ActionBarButton, ActionBarClear, ActionBarCount, Column, Count, MiniButton, PanelActions, Toolbar, ToolbarNote,
  Toggle, Workspace,
} from './programAdminStyles';

// The Program area (plan §7.7 items 2 and 4): the day tabs, the timeline of the selected day, the
// "needs attention" filter and the "show removed" switch, the sticky bar for the ticked spots, and
// the side panel of whatever is selected (a spot, an entry or a person; the person's panel is wider). It holds no state of its own - every value and callback
// comes from the screen that owns the view state. Above the timeline, the program's own pages: title
// pages and sponsor pages, with where the sponsor pages stand (PdfPagesEntries). Below the day tabs,
// "Add block" for a new top-level entry (offered while the event has a day to put it on).
const FILTER_LABELS = {
  open: 'Needs filling', toInvite: 'To invite', invited: 'Invited', declined: 'Declined', confirmed: 'Confirmed',
};

// What each filter keeps, as the chip's tooltip.
const FILTER_HINTS = {
  open: 'Spots nobody is on, and spots whose person declined, withdrew or was uninvited',
  toInvite: 'A person is on the spot but has not been asked yet',
  invited: 'Asked and no answer yet',
  declined: 'The person said no; the spot needs filling again',
  confirmed: 'The person said yes',
};

const ProgramArea = ({
  eventId, program, language, people = [], selection, dayId, checked = new Set(), spotFilter = null,
  showRemoved = false, filterCounts = {}, entryPosition,
  onSelectDay, onToggleChecked, onSelectFilter, onToggleShowRemoved, onSelectEntry, onSelectSpot,
  onClosePanel, onOpenPerson, onInviteSelected, onClearSelection, spotActions, entryActions, onAddBlock, onChanged,
  onDownloadMaterials,
}) => {
  const intl = useIntl();
  const days = daysOf(program);
  const currentDay = days.find((d) => d.id === dayId) || days[0] || null;
  const totalSpots = spotCount(program);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const downloadMaterials = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      await onDownloadMaterials(eventId);
    } catch {
      setDownloadError(intl.formatMessage({
        id: 'programAdmin.materials.downloadFailed', defaultMessage: 'Could not download the materials.',
      }));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Workspace $panel={!!selection} $wide={selection?.kind === 'person'}>
      <Column>
        {eventId != null && <PdfPagesEntries eventId={eventId} program={program} language={language} />}
        <Toolbar>
          <Filter size={13} aria-hidden="true" style={{ color: '#64748b' }} />
          {/* One quick filter at a time: ticking one clears the others, unticking shows everything. */}
          {SPOT_FILTERS.map((key) => (
            <Toggle key={key} $active={spotFilter === key}
              title={intl.formatMessage({ id: `programAdmin.filter.${key}.hint`, defaultMessage: FILTER_HINTS[key] })}>
              <input type="checkbox" checked={spotFilter === key} onChange={(e) => onSelectFilter?.(e.target.checked ? key : null)} />
              {intl.formatMessage({ id: `programAdmin.filter.${key}`, defaultMessage: FILTER_LABELS[key] })}
              <Count>{filterCounts[key] ?? 0}</Count>
            </Toggle>
          ))}
          <Toggle $active={showRemoved}>
            <input type="checkbox" checked={showRemoved} onChange={(e) => onToggleShowRemoved?.(e.target.checked)} />
            <FormattedMessage id="programAdmin.filter.showRemoved" defaultMessage="Show removed" />
          </Toggle>
          <ToolbarNote>
            <FormattedMessage id="programAdmin.filter.summary"
              defaultMessage="{count, plural, one {# spot} other {# spots}} on this event"
              values={{ count: totalSpots }} />
          </ToolbarNote>
        </Toolbar>

        <DayTabsBar days={days} activeId={currentDay?.id ?? null} language={language} onSelect={onSelectDay} />

        {(onDownloadMaterials || (onAddBlock && (program?.days || []).length > 0)) && (
          <PanelActions style={{ marginBottom: '.75rem', flexWrap: 'wrap' }}>
            {onAddBlock && (program?.days || []).length > 0 && (
              <MiniButton type="button" onClick={() => onAddBlock(currentDay?.id ?? null)}>
                <Plus size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.day.addBlock" defaultMessage="Add block" />
              </MiniButton>
            )}
            {onDownloadMaterials && (
              <MiniButton type="button" disabled={downloading} onClick={downloadMaterials}>
                <FileDown size={13} aria-hidden="true" />
                {downloading
                  ? <FormattedMessage id="programAdmin.materials.downloading" defaultMessage="Preparing the download…" />
                  : <FormattedMessage id="programAdmin.materials.downloadAll" defaultMessage="Download all materials (zip)" />}
              </MiniButton>
            )}
            {downloadError && <span role="alert" style={{ color: '#991b1b', fontSize: '.85rem' }}>{downloadError}</span>}
          </PanelActions>
        )}

        {currentDay && (
          <ProgramList
            program={program}
            language={language}
            dayId={currentDay.id}
            checked={checked}
            selection={selection}
            spotFilter={spotFilter}
            showRemoved={showRemoved}
            people={people}
            onSelectEntry={onSelectEntry}
            onSelectSpot={onSelectSpot}
            onOpenPerson={onOpenPerson}
            onToggleChecked={onToggleChecked}
            actions={spotActions}
          />
        )}

        {checked.size > 0 && (
          <ActionBar role="region" aria-label={intl.formatMessage({ id: 'programAdmin.selection.bar', defaultMessage: 'Selected spots' })}>
            <ActionBarCount>
              <FormattedMessage id="programAdmin.selection.count"
                defaultMessage="{count, plural, one {# spot selected} other {# spots selected}}"
                values={{ count: checked.size }} />
            </ActionBarCount>
            <ActionBarButton type="button" onClick={() => onInviteSelected?.([...checked])}>
              <FormattedMessage id="programAdmin.selection.invite" defaultMessage="Invite selected ({count})" values={{ count: checked.size }} />
            </ActionBarButton>
            <ActionBarClear type="button" onClick={onClearSelection}>
              <X size={13} aria-hidden="true" />
              <FormattedMessage id="programAdmin.selection.clear" defaultMessage="Clear" />
            </ActionBarClear>
          </ActionBar>
        )}
      </Column>

      <SidePanel
        program={program}
        language={language}
        selection={selection}
        people={people}
        entryPosition={entryPosition}
        spotActions={spotActions}
        entryActions={entryActions}
        onOpenPerson={onOpenPerson}
        onOpenSpot={(assignmentId) => onSelectSpot?.({ id: assignmentId })}
        onChanged={onChanged}
        onClose={onClosePanel}
      />
    </Workspace>
  );
};

export default ProgramArea;
